'use client'

import { useQueryClient } from '@tanstack/react-query'
import { createContext, type ReactNode, useContext, useState } from 'react'

interface IQueryContext {
  addKey: (key: (string | undefined)[]) => void
  clear: () => void
  keys: (string | undefined)[][]
  refetch: () => void
  removeKey: (key: string[]) => void
}

export const QueryContext = createContext<IQueryContext>({} as IQueryContext)

export const QueryContextProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient()
  const [keys, setKeys] = useState<(string | undefined)[][]>([])

  const addKey = (key: (string | undefined)[]) => {
    setKeys([...keys, key])
  }

  const removeKey = (key: string[]) => {
    setKeys(keys.filter(k => k !== key))
  }

  const refetch = async () => {
    await Promise.all(keys.map(key => queryClient.invalidateQueries({ queryKey: key })))
  }

  const clear = () => {
    setKeys([])
  }

  return (
    <QueryContext.Provider
      value={{
        addKey,
        clear,
        keys,
        refetch,
        removeKey,
      }}
    >
      {children}
    </QueryContext.Provider>
  )
}

export const useQueryContext = () => {
  const context = useContext(QueryContext)

  if (!context) {
    throw new Error('useQueryContext must be used within a QueryContextProvider')
  }

  return context
}
